import { useSearchParams } from "react-router-dom";
import { useCallback, useMemo } from "react";
import type { FindingKind } from "./workspace-types";

export type WorkspaceTab = "findings" | "trace" | "compare";

export function useWorkspaceSearchParams() {
  const [searchParams, setSearchParams] = useSearchParams();

  const tabParam = searchParams.get("tab");
  const stepParam = searchParams.get("step");
  const stepNumber = stepParam ? parseInt(stepParam, 10) : null;

  const activeTab: WorkspaceTab = useMemo(() => {
    if (tabParam === "compare") return "compare";
    if (tabParam === "trace" || stepNumber !== null) return "trace";
    return "findings";
  }, [tabParam, stepNumber]);

  const statusParam = searchParams.get("status");
  const hasExplicitStatusFilter = statusParam !== null;
  const statusFilter = statusParam ?? "open";
  const kindFilter = (searchParams.get("kind") as FindingKind) ?? "problem";
  const severityFilter = searchParams.get("severity") ?? "all";
  const selectedFindingId = searchParams.get("finding");
  const baseRunId = searchParams.get("base");
  const reportOpen = searchParams.get("report") === "true";

  const setActiveTab = useCallback(
    (tab: WorkspaceTab) => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          if (tab === "findings") {
            next.delete("tab");
            next.delete("step");
          } else if (tab === "compare") {
            next.set("tab", "compare");
            next.delete("step");
          } else {
            next.set("tab", "trace");
          }
          return next;
        },
        { replace: true },
      );
    },
    [setSearchParams],
  );

  const setStatusFilter = useCallback(
    (status: string) => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          next.set("status", status);
          return next;
        },
        { replace: true },
      );
    },
    [setSearchParams],
  );

  const setKindFilter = useCallback(
    (kind: FindingKind) => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          if (kind === "problem") {
            next.delete("kind");
          } else {
            next.set("kind", kind);
          }
          return next;
        },
        { replace: true },
      );
    },
    [setSearchParams],
  );

  const setSeverityFilter = useCallback(
    (sev: string) => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          if (sev === "all") {
            next.delete("severity");
          } else {
            next.set("severity", sev);
          }
          return next;
        },
        { replace: true },
      );
    },
    [setSearchParams],
  );

  const selectFinding = useCallback(
    (findingId: string | null) => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          if (!findingId) {
            next.delete("finding");
          } else {
            next.set("finding", findingId);
          }
          return next;
        },
        { replace: true },
      );
    },
    [setSearchParams],
  );

  const selectBaseRun = useCallback(
    (baseId: string | null) => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          if (!baseId) {
            next.delete("base");
          } else {
            next.set("base", baseId);
          }
          return next;
        },
        { replace: true },
      );
    },
    [setSearchParams],
  );

  const setReportOpen = useCallback(
    (open: boolean) => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          if (open) {
            next.set("report", "true");
          } else {
            next.delete("report");
          }
          return next;
        },
        { replace: true },
      );
    },
    [setSearchParams],
  );

  return {
    activeTab,
    setActiveTab,
    statusFilter,
    hasExplicitStatusFilter,
    setStatusFilter,
    kindFilter,
    setKindFilter,
    severityFilter,
    setSeverityFilter,
    selectedFindingId,
    selectFinding,
    stepNumber,
    baseRunId,
    selectBaseRun,
    reportOpen,
    setReportOpen,
    searchParams,
    setSearchParams,
  };
}
